<script setup lang="ts">
import type { CSSProperties } from "vue";
import type { IconName } from "../Icon/icons";
import { computed, ref, watch } from "vue";
import { i18n } from "../../translations/i18n";
import VIcon from "../Icon/VIcon.vue";
import {
  tagCloseCva,
  tagContainerCva,
  tagIconCva,
  tagTextCva,
} from "./VTag.cva";

const {
  text = "",
  variant = "is-primary",
  closeable = false,
  icon = "",
  disabled = false,
  color = null,
  backgroundColor = null,
  size = "small",
  cursor = false,
  iconLess = false,
  selectable = false,
  dataValue = "",
  closeLabel = "",
} = defineProps<{
  text?: string
  variant?: string
  closeable?: boolean
  icon?: IconName
  disabled?: boolean
  color?: string | null
  backgroundColor?: string | null
  size?: string
  cursor?: boolean
  iconLess?: boolean
  selectable?: boolean
  dataValue?: string
  /** Accessible name of the close button, e.g. "Filter periode wissen". Defaults to the generic "Remove". */
  closeLabel?: string
}>();

const emit = defineEmits<{
  close: []
}>();

const model = defineModel<boolean>("modelValue", { default: false });

const isChecked = ref(model.value);

watch(() => model.value, (newValue) => {
  isChecked.value = newValue;
});

function toggleCheck() {
  if (!selectable) {
    return;
  }
  isChecked.value = !isChecked.value;
  model.value = isChecked.value;
}

// Only the tag itself toggles: keys typed inside a slotted control must keep their own meaning.
function onKeyToggle(event: KeyboardEvent) {
  if (!selectable || event.target !== event.currentTarget) {
    return;
  }
  event.preventDefault();
  toggleCheck();
}

const removeLabel = computed(() => closeLabel || String(i18n.global.t("base.remove")));

const tagStyle = computed<CSSProperties>(() => ({
  backgroundColor: backgroundColor ?? undefined,
  color: color ?? undefined,
}));

const classes = computed(() => ({
  container: tagContainerCva({
    variant: variant as "is-primary" | "is-secondary" | "is-info" | "is-warning" | "is-danger" | "is-success" | "is-gray" | "is-subtle" | "is-text-color",
    size: size as "extra-small" | "small" | "medium" | "large",
    cursor: cursor || selectable,
    selectable,
    checked: isChecked.value,
  }),
  icon: tagIconCva({}),
  text: tagTextCva({}),
  close: tagCloseCva({
    disabled,
  }),
}));
</script>

<template>
  <div
    :style="tagStyle"
    :data-value="dataValue"
    :class="[classes.container, selectable ? 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500' : '']"
    :role="selectable ? 'checkbox' : undefined"
    :aria-checked="selectable ? isChecked : undefined"
    :tabindex="selectable ? 0 : undefined"
    @click="toggleCheck"
    @keydown.space="onKeyToggle"
    @keydown.enter="onKeyToggle"
  >
    <div v-if="(icon || $slots.icon) && !iconLess" :class="classes.icon">
      <slot name="icon">
        <VIcon v-if="icon" :key="icon" :name="icon" aria-hidden="true" />
      </slot>
    </div>

    <div v-if="text" :class="classes.text">
      {{ text }}
    </div>

    <slot name="suffix" />

    <button
      v-if="closeable && !disabled && !iconLess"
      type="button"
      data-test="close-icon"
      class="rounded-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
      :class="[classes.close]"
      :aria-label="removeLabel"
      @click.stop="emit('close')"
    >
      <VIcon name="x" aria-hidden="true" />
    </button>
  </div>
</template>
